import { Component, DestroyRef } from '@angular/core';
import { otpHandler } from 'authentication/functions/otp-handler';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { filter, of, switchMap } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from 'shared/services';
import { ToastService } from 'shared/services/toast.service';
import { passwordPattern } from 'shared/validators/password-pattern-validator';
import { mustMatch } from 'shared/validators/must-match-validator';
import { UserApiService } from 'shared/services/userApi.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-reset-password-form',
    templateUrl: './reset-password-form.component.html',
    styleUrls: ['./reset-password-form.component.scss', '../../authentication.scss'],
    standalone: false,
})
export class ResetPasswordFormComponent {
    public requestData!: any;
    public requestDataLoaded: boolean = false;
    public resetPasswordForm = new UntypedFormGroup({
        newPassword: new UntypedFormControl('', [Validators.required, Validators.minLength(8), passwordPattern()]),
        confirmPassword: new UntypedFormControl('', [Validators.required, mustMatch('newPassword')]),
    });

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userApiService: UserApiService,
        private destroyRef: DestroyRef,
        private toastService: ToastService,
        private modalService: ModalService,
        private authenticationService: AuthenticationService,
    ) {
        this.route.params
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((params) => {
                    if (params?.requestId) {
                        return this.userApiService.getRequestData(params.requestId);
                    }
                    return of(null);
                }),
            )
            .subscribe((requestData) => {
                this.requestDataLoaded = true;
                this.requestData = requestData;
            });
    }

    public resetPassword() {
        this.resetPasswordForm.markAllAsTouched();
        this.resetPasswordForm.get('newPassword')?.updateValueAndValidity();
        this.resetPasswordForm.get('confirmPassword')?.updateValueAndValidity();

        if (this.resetPasswordForm.valid) {
            const body = {
                requestId: this.requestData?.id,
                email: this.requestData?.email ?? '',
                password: this.resetPasswordForm.value.newPassword,
            };

            this.userApiService
                .resetPassword(body)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    switchMap(() => {
                        if (!body.email && !body.password) {
                            return of(undefined);
                        }

                        return this.authenticationService.login(body);
                    }),
                    filter(Boolean),
                    filter((response) => {
                        return otpHandler(response, body, this.toastService, this.modalService, this.router, this.destroyRef);
                    }),
                )
                .subscribe(() => {
                    this.toastService.showToast({
                        body: 'changePassword.changeSuccess',
                        severity: 'success',
                        ignoreRemoval: true,
                    });
                    this.router.navigate(['/']);
                });
        }
    }
}
